import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Check, 
  Star, 
  Zap, 
  Crown, 
  Users, 
  Calculator, 
  TrendingUp, 
  Shield, 
  Clock, 
  Award, 
  ArrowRight, 
  Phone, 
  ChevronDown,
  ChevronUp,
  DollarSign,
  Building2,
  Rocket,
  Target,
  Layers,
  X,
  CheckCircle
} from 'lucide-react';

const Pricing = () => {
  const [selectedTier, setSelectedTier] = useState(1);
  const [licenseCount, setLicenseCount] = useState(20);
  const [billingCycle, setBillingCycle] = useState('6month'); // '6month' or '12month'
  const [expandedFaq, setExpandedFaq] = useState(null);

  // License pricing tiers
  const pricingTiers = [
    { range: '1-10', licenses: 10, pricePerLicense: 85, savings: 0, color: 'from-blue-500 to-cyan-500' },
    { range: '11-20', licenses: 20, pricePerLicense: 80, savings: 100, color: 'from-green-500 to-emerald-500' },
    { range: '21-50', licenses: 50, pricePerLicense: 70, savings: 750, color: 'from-purple-500 to-pink-500' },
    { range: '51+', licenses: 100, pricePerLicense: 60, savings: 2000, color: 'from-orange-500 to-red-500', enterprise: true }
  ];

  const features = [
    { name: 'AI-Powered Automation', included: true },
    { name: 'No-Code Platform', included: true },
    { name: 'Real-time Analytics', included: true },
    { name: 'Mobile Responsive Apps', included: true },
    { name: 'Enterprise Security', included: true },
    { name: '24/7 Priority Support', included: true },
    { name: 'Custom Integrations', included: true },
    { name: 'White-label Solutions', included: true },
    { name: 'Advanced Workflow Builder', included: true },
    { name: 'Multi-tenant Architecture', included: true },
    { name: 'API Access & Webhooks', included: true },
    { name: 'Dedicated Account Manager', included: false, enterpriseOnly: true }
  ];

  const testimonials = [
    {
      name: '',
      title: '',
      company: 'TechCorp Inc.',
      quote: 'Aquargin saved us 60% on development costs and 8 months of development time.',
      avatar: '👩‍💼',
      savings: '$240K'
    },
    {
      name: '',
      title: '',
      company: 'Global Systems',
      quote: 'ROI was visible within the first month. Our efficiency increased by 300%.',
      avatar: '👨‍💼',
      roi: '300%'
    },
    {
      name: '',
      title: '',
      company: 'HealthTech Solutions',
      quote: 'Managing users across multiple departments has never been easier.',
      avatar: '👩‍⚕️',
      users: 'Enterprise'
    }
  ];

  const faqs = [
    {
      question: 'How does the license pricing work?',
      answer: 'Each license covers one user in your organization. Volume discounts apply automatically - the more licenses you need, the lower the per-license cost. For example, if you need 25 licenses, you pay $70 per license per month.'
    },
    {
      question: 'Can I add more licenses later?',
      answer: 'Absolutely! You can add licenses anytime and they\'ll be prorated for the current billing period. Volume discounts apply immediately when you reach the next pricing tier.'
    },
    {
      question: 'What\'s included in the 30-day free trial?',
      answer: 'Full access to all features for up to 10 users, including AI automation, custom app building, integrations, and priority support. No credit card required to start.'
    },
    {
      question: 'What are the billing options and discounts?',
      answer: 'We offer 6-month and 12-month billing cycles. The prices shown are for 6-month billing. Choose 12-month billing to save 5% on all license tiers. Enterprise customers get additional volume discounts.'
    },
    {
      question: 'What happens if I exceed my license count?',
      answer: 'We\'ll notify you when you\'re approaching your limit. You can easily upgrade to the next tier or add individual licenses. We never shut down your service unexpectedly.'
    },
    {
      question: 'Is there a setup fee or hidden costs?',
      answer: 'No setup fees or hidden costs for the platform licenses. All standard features, security, hosting, and support are included. However, configuration, customization, and implementation services are quoted separately based on your specific requirements.'
    },
    {
      question: 'What additional services are available?',
      answer: 'Beyond the standard platform, we offer: Custom configuration and setup services, tailored feature development, advanced integrations, dedicated training programs, and implementation consulting. These services are priced separately and customized to your specific business needs.'
    }
  ];

  const calculateROI = (licenses, billing = billingCycle) => {
    const basePricePerLicense = pricingTiers.find(tier => {
      if (tier.range.includes('+')) {
        const min = parseInt(tier.range.split('+')[0]);
        return licenses >= min;
      } else {
        const [min, max] = tier.range.split('-').map(n => parseInt(n));
        return licenses >= min && licenses <= max;
      }
    })?.pricePerLicense || 85;
    
    // Apply 5% discount for 12-month billing
    const pricePerLicense = billing === '12month' ? basePricePerLicense * 0.95 : basePricePerLicense;
    const monthlyCost = licenses * pricePerLicense;
    
    const traditionalCost = licenses * 200; // Assume $200 per user for traditional solutions
    const savings = traditionalCost - monthlyCost;
    const roiPercentage = Math.round((savings / monthlyCost) * 100);
    
    return { 
      monthlyCost, 
      savings, 
      roiPercentage, 
      pricePerLicense: Math.round(pricePerLicense * 100) / 100,
      basePricePerLicense,
      discount: billing === '12month' ? 5 : 0
    };
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Urgency */}
      <section className="relative bg-gradient-to-br from-primary/10 via-white to-secondary/5 py-20 lg:py-32 overflow-hidden">
        <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold animate-pulse">
          🔥 Limited Time: 30% OFF First 3 Months
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full mb-4">
                <Award className="h-4 w-4 mr-2" />
                <span className="text-sm font-semibold">Trusted by Enterprise Clients Worldwide</span>
              </div>

              <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight">
                Scale Smart with
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-secondary-500">
                  Volume Pricing
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed">
                The more licenses you need, the more you save. Enterprise automation 
                that scales with your business - from startups to Fortune 500.
              </p>

              <div className="flex flex-wrap justify-center gap-6 pt-4">
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700 font-medium">30-Day Free Trial</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700 font-medium">No Setup Fees</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700 font-medium">Cancel Anytime</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Calculate Your Savings
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how much you'll save compared to traditional development and licensing costs
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-xl border">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    <Calculator className="inline h-6 w-6 mr-2 text-primary" />
                    Volume Pricing Calculator
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Billing Cycle Toggle */}
                    <div className="flex items-center justify-center mb-6">
                      <div className="bg-gray-100 p-1 rounded-lg flex">
                        <button
                          onClick={() => setBillingCycle('6month')}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                            billingCycle === '6month'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          6-Month Billing
                        </button>
                        <button
                          onClick={() => setBillingCycle('12month')}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-all relative ${
                            billingCycle === '12month'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          12-Month Billing
                          <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                            5% OFF
                          </span>
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">
                        How many licenses do you need?
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="1"
                          max="500"
                          value={licenseCount}
                          onChange={(e) => {
                            const value = Math.max(1, Math.min(500, parseInt(e.target.value) || 1));
                            setLicenseCount(value);
                          }}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-center font-bold text-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Licenses</span>
                      </div>
                    </div>
                    
                    <div className="relative">
                      <input
                        type="range"
                        min="1"
                        max="200"
                        value={Math.min(licenseCount, 200)}
                        onChange={(e) => setLicenseCount(parseInt(e.target.value))}
                        className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider transition-all duration-150 ease-out"
                        style={{
                          background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${(Math.min(licenseCount, 200) / 200) * 100}%, #E5E7EB ${(Math.min(licenseCount, 200) / 200) * 100}%, #E5E7EB 100%)`,
                          WebkitAppearance: 'none',
                          MozAppearance: 'none'
                        }}
                      />
                    </div>
                    
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>1 License</span>
                      <span>200+ Licenses</span>
                    </div>

                    {/* Real-time Calculations Display */}
                    {(() => {
                      const sliderRoi = calculateROI(licenseCount, billingCycle);
                      const tierInfo = pricingTiers.find(tier => {
                        if (tier.range.includes('+')) {
                          const min = parseInt(tier.range.split('+')[0]);
                          return licenseCount >= min;
                        } else {
                          const [min, max] = tier.range.split('-').map(n => parseInt(n));
                          return licenseCount >= min && licenseCount <= max;
                        }
                      }) || pricingTiers[pricingTiers.length - 1];
                      
                      return (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Per License</p>
                              <div className="flex flex-col">
                                <span className="text-lg font-bold text-gray-900">
                                  ${sliderRoi.pricePerLicense}
                                </span>
                                {sliderRoi.discount > 0 && (
                                  <span className="text-xs text-green-600 font-medium">
                                    {sliderRoi.discount}% off
                                  </span>
                                )}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Monthly Total</p>
                              <span className="text-lg font-bold text-primary">
                                ${sliderRoi.monthlyCost.toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Monthly Savings</p>
                              <span className="text-lg font-bold text-green-600">
                                ${sliderRoi.savings.toLocaleString()}
                              </span>
                            </div>
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">
                                Tier: <strong>{tierInfo.range} licenses</strong>
                              </span>
                              <span className="text-gray-600">
                                Billing: <strong>{billingCycle === '6month' ? '6-Month' : '12-Month'}</strong>
                              </span>
                              <span className="text-green-600 font-medium">
                                ROI: <strong>{sliderRoi.roiPercentage}%</strong>
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    
                                         {licenseCount > 200 && (
                       <div className="text-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                         <p className="text-sm text-yellow-800">
                           <strong>Enterprise Volume:</strong> For {licenseCount} licenses, 
                           <a href="/contact" className="text-primary-600 hover:underline ml-1">contact our sales team</a> for custom pricing.
                         </p>
                       </div>
                     )}

                     {/* Configuration & Customization Notice */}
                     <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                       <div className="flex items-start space-x-3">
                         <div className="flex-shrink-0">
                           <span className="text-blue-600 text-lg">⚙️</span>
                         </div>
                         <div>
                           <h4 className="text-sm font-semibold text-blue-900 mb-2">
                             Additional Services Available
                           </h4>
                           <div className="text-sm text-blue-800 space-y-1">
                             <p>• <strong>Configuration & Setup:</strong> Custom implementation services</p>
                             <p>• <strong>Customization:</strong> Tailored features and integrations</p>
                             <p>• <strong>Training & Onboarding:</strong> Dedicated team training programs</p>
                           </div>
                           <p className="text-xs text-blue-700 mt-2">
                             <em>These services are quoted separately based on your specific requirements.</em>
                           </p>
                         </div>
                       </div>
                     </div>
                  </div>

                  {(() => {
                    const roi = calculateROI(licenseCount, billingCycle);
                    const tierInfo = pricingTiers.find(tier => {
                      if (tier.range.includes('+')) return licenseCount >= 151;
                      const [min, max] = tier.range.split('-').map(n => parseInt(n));
                      return licenseCount >= min && licenseCount <= max;
                    }) || pricingTiers[pricingTiers.length - 1];
                    
                    return (
                      <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-gray-600">Monthly Cost</p>
                            <p className="text-2xl font-bold text-gray-900">${roi.monthlyCost.toLocaleString()}</p>
                            <div className="flex items-center space-x-2">
                              <p className="text-xs text-gray-500">${roi.pricePerLicense}/license</p>
                              {roi.discount > 0 && (
                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                  {roi.discount}% OFF
                                </span>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Monthly Savings</p>
                            <p className="text-2xl font-bold text-green-600">${roi.savings.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">vs traditional solutions</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-sm text-gray-600">Annual ROI</p>
                            <p className="text-3xl font-bold text-green-600">{roi.roiPercentage}%</p>
                          </div>
                        </div>
                        <div className="text-center pt-4 border-t border-green-200">
                          <div className="flex items-center justify-center space-x-4 text-sm text-green-700">
                            <span>
                              <strong>Pricing Tier:</strong> {tierInfo.range} licenses
                            </span>
                            <span>
                              <strong>Billing:</strong> {billingCycle === '6month' ? '6-Month' : '12-Month'}
                            </span>
                            {billingCycle === '6month' && (
                              <span className="text-xs text-gray-600">
                                (Base pricing shown)
                              </span>
                            )}
                          </div>
                          {tierInfo.savings > 0 && (
                            <div className="mt-2">
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                                Volume Discount: Save ${tierInfo.savings.toLocaleString()}/month
                              </span>
                            </div>
                          )}
                          <div className="mt-3 pt-2 border-t border-green-100">
                            <p className="text-xs text-gray-600">
                              <strong>Note:</strong> Configuration, customization, and implementation services are quoted separately.
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900">What's included in every license:</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {features.slice(0, 8).map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <Check className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Volume Pricing Tiers
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              Transparent pricing that scales with your business. The more you need, the more you save.
            </p>
            <div className="inline-flex items-center bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <span className="text-sm text-blue-800">
                <strong>Pricing shown:</strong> 6-month billing cycle • 
                <span className="text-green-600 font-medium ml-1">Save 5% with 12-month billing</span>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.range}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl p-8 shadow-xl border-2 transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                  index === 2 ? 'border-primary-500 ring-4 ring-primary-100' : 'border-gray-200'
                }`}
              >
                {index === 2 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-500 text-white px-6 py-2 rounded-full text-sm font-bold">
                      🚀 Most Popular
                    </span>
                  </div>
                )}

                {tier.savings > 0 && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    Save ${tier.savings.toLocaleString()}/mo
                  </div>
                )}

                <div className={`w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r ${tier.color} flex items-center justify-center`}>
                  <Users className="w-8 h-8 text-white" />
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.range} Licenses</h3>
                  <div className="space-y-2">
                    {/* 6-Month Pricing */}
                    <div>
                      <div className="text-4xl font-bold text-gray-900">
                        ${tier.pricePerLicense}
                        <span className="text-lg text-gray-500 font-normal">/license/month</span>
                      </div>
                      <p className="text-sm text-gray-600">6-month billing</p>
                    </div>
                    
                    {/* 12-Month Pricing */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="text-2xl font-bold text-green-700">
                        ${Math.round(tier.pricePerLicense * 0.95 * 100) / 100}
                        <span className="text-sm text-green-600 font-normal">/license/month</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        <p className="text-xs text-green-600">12-month billing</p>
                        <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                          5% OFF
                        </span>
                      </div>
                    </div>
                    
                    {index > 0 && (
                      <p className="text-green-600 font-semibold text-sm">
                        Save ${85 - tier.pricePerLicense}/license vs starter pricing
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-3 mb-8">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700">Monthly Cost (Max)</span>
                    <span className="font-bold text-gray-900">
                      ${(tier.licenses * tier.pricePerLicense).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span className="text-gray-700">Annual Savings</span>
                    <span className="font-bold text-green-600">
                      ${(tier.savings * 12).toLocaleString()}
                    </span>
                  </div>
                </div>

                <motion.a
                  href={tier.enterprise ? "/contact" : "https://app.aquargin.com/register"}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center ${
                    index === 2
                      ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg hover:shadow-xl'
                      : tier.enterprise
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
                >
                  {tier.enterprise ? (
                    <>
                      <Phone className="inline h-5 w-5 mr-2" />
                      Contact Sales
                    </>
                  ) : (
                    <>
                      Start Free Trial
                      <ArrowRight className="inline h-5 w-5 ml-2" />
                    </>
                  )}
                </motion.a>

                {!tier.enterprise && (
                  <div className="text-center mt-3 space-y-1">
                    <p className="text-sm text-gray-500">
                      30-day free trial • No credit card required
                    </p>
                    <p className="text-xs text-gray-400">
                      Configuration & customization services quoted separately
                    </p>
                  </div>
                )}
                
                {tier.enterprise && (
                  <p className="text-center text-xs text-gray-400 mt-3">
                    Includes dedicated implementation support
                  </p>
                )}
              </motion.div>
            ))}
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 text-center">
            <div className="flex flex-wrap justify-center items-center gap-8 opacity-70">
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-sm font-medium">Enterprise Security</span>
              </div>
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-blue-500 mr-2" />
                <span className="text-sm font-medium">99.9% Uptime SLA</span>
              </div>
              <div className="flex items-center">
                <Award className="h-5 w-5 text-purple-500 mr-2" />
                <span className="text-sm font-medium">G2 Leader 2024</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Composable Value Proposition */}
      <section className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Composable Architecture ROI
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              Organizations adopting composable enterprise architecture report 30% higher revenue growth. 
              Build with modular components that adapt to change and accelerate innovation.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg text-center"
            >
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">30% Faster Innovation</h3>
              <p className="text-gray-600">Packaged business capabilities enable rapid assembly of new solutions without starting from scratch.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg text-center"
            >
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Layers className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Modular Scalability</h3>
              <p className="text-gray-600">Scale individual components independently, optimize resources, and adapt to changing business needs seamlessly.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg text-center"
            >
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">API-First Integration</h3>
              <p className="text-gray-600">Every component exposes standardized APIs, enabling seamless orchestration across your enterprise ecosystem.</p>
            </motion.div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Composable vs Traditional Architecture</h3>
              <p className="text-gray-600">See the difference composable systems make for enterprise agility</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="text-lg font-bold text-gray-900 mb-4">🏗️ Traditional Monolithic</h4>
                <div className="space-y-3">
                  <div className="flex items-center text-red-600">
                    <X className="h-4 w-4 mr-2" />
                    <span className="text-sm">Rigid, difficult to change</span>
                  </div>
                  <div className="flex items-center text-red-600">
                    <X className="h-4 w-4 mr-2" />
                    <span className="text-sm">All-or-nothing scaling</span>
                  </div>
                  <div className="flex items-center text-red-600">
                    <X className="h-4 w-4 mr-2" />
                    <span className="text-sm">Slow innovation cycles</span>
                  </div>
                  <div className="flex items-center text-red-600">
                    <X className="h-4 w-4 mr-2" />
                    <span className="text-sm">High technical debt</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-lg font-bold text-gray-900 mb-4">🧩 Composable Architecture</h4>
                <div className="space-y-3">
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span className="text-sm">Modular, adaptable components</span>
                  </div>
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span className="text-sm">Independent scaling</span>
                  </div>
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span className="text-sm">Rapid feature delivery</span>
                  </div>
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span className="text-sm">Reduced complexity</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof & Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Join Companies Saving Millions
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See why industry leaders choose Aquargin for their composable enterprise transformation
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 text-lg leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-3xl mr-3">{testimonial.avatar}</div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.title}</div>
                      <div className="text-sm text-gray-500">{testimonial.company}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    {testimonial.savings && (
                      <div className="text-2xl font-bold text-green-600">{testimonial.savings}</div>
                    )}
                    {testimonial.roi && (
                      <div className="text-2xl font-bold text-blue-600">{testimonial.roi}</div>
                    )}
                    {testimonial.users && (
                      <div className="text-2xl font-bold text-purple-600">{testimonial.users}</div>
                    )}
                    <div className="text-xs text-gray-500">
                      {testimonial.savings ? 'Saved' : testimonial.roi ? 'ROI' : 'Users'}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { number: 'Global', label: 'Enterprise Clients', icon: Building2 },
              { number: '1000+', label: 'Apps Built', icon: Rocket },
              { number: '99.9%', label: 'Uptime SLA', icon: Shield },
              { number: '60%', label: 'Avg Cost Savings', icon: TrendingUp }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="flex justify-center mb-3">
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-1">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Frequently Asked Questions
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to know about our pricing and platform
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <button
                  className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  {expandedFaq === index ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compelling CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <h2 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Ready to Transform Your Business?
                </h2>
                <p className="text-xl lg:text-2xl text-white/90 leading-relaxed">
                  Join companies already saving millions with Aquargin's automation platform.
                  Start your free trial today - no credit card required.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-primary px-10 py-5 rounded-xl font-bold text-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center group"
                >
                  <Rocket className="mr-3 h-6 w-6 group-hover:animate-bounce" />
                  Start 30-Day Free Trial
                  <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-white hover:text-primary transition-all duration-300 flex items-center justify-center group"
                >
                  <Phone className="mr-3 h-6 w-6" />
                  Talk to Sales Expert
                </motion.button>
              </div>

              <div className="flex flex-wrap justify-center gap-8 pt-6 text-white/80">
                <div className="flex items-center">
                  <Check className="h-5 w-5 mr-2" />
                  <span>30-day money-back guarantee</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-5 w-5 mr-2" />
                  <span>Setup in under 5 minutes</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-5 w-5 mr-2" />
                  <span>No long-term contracts</span>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mt-8">
                <div className="text-white/90 text-sm mb-2">🔥 Limited Time Offer</div>
                <div className="text-white font-bold text-lg">
                  Sign up this month and get your first 3 months at 30% off + free migration support
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Custom Slider Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .slider::-webkit-slider-thumb {
            appearance: none;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: #3B82F6;
            border: 3px solid white;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
            cursor: pointer;
            transition: all 0.2s ease;
          }
          
          .slider::-webkit-slider-thumb:hover {
            transform: scale(1.1);
            box-shadow: 0 6px 16px rgba(59, 130, 246, 0.6);
          }
          
          .slider::-webkit-slider-thumb:active {
            transform: scale(1.05);
          }
          
          .slider::-moz-range-thumb {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: #3B82F6;
            border: 3px solid white;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
          }
          
          .slider::-moz-range-thumb:hover {
            transform: scale(1.1);
            box-shadow: 0 6px 16px rgba(59, 130, 246, 0.6);
          }
          
          .slider:focus {
            outline: none;
          }
          
          .slider:focus::-webkit-slider-thumb {
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4), 0 0 0 2px #3B82F6;
          }
        `
      }} />
    </div>
  );
};

export default Pricing; 